import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import axios from 'axios';
import '../components/ProtectedSensitiveData.css';

const API_URL = import.meta.env.VITE_API_URL;
const SENSITIVE_LOCK_ENABLED = true;

const STORAGE_KEY = 'sastra_sensitive_data_unlock';
const WARNING_MESSAGE = 'Mot de passe incorrect. Veuillez vérifier vos informations et réessayer.';

const SensitiveDataLockContext = createContext(null);

const readStoredSession = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.unlocked || !parsed?.expiresAt) return null;

    const expiresAt = Number(parsed.expiresAt);
    if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return { unlocked: true, expiresAt };
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const getRemainingMs = (expiresAt) => Math.max(0, Number(expiresAt || 0) - Date.now());

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

export const SensitiveDataLockProvider = ({ children }) => {
  const [session, setSession] = useState(() => readStoredSession());
  const [lastError, setLastError] = useState('');
  const [lastSuccess, setLastSuccess] = useState('');
  const [remainingMs, setRemainingMs] = useState(() => getRemainingMs(readStoredSession()?.expiresAt));
  const [actionsCatalog, setActionsCatalog] = useState([]);
  const [userExceptionalAuths, setUserExceptionalAuths] = useState([]);
  const [securityStatus, setSecurityStatus] = useState({
    hasPasswordSet: false,
    isLockedOut: false,
    lockoutRemainingMinutes: 0,
    failedAttemptsCount: 0,
    maxFailedAttempts: 5
  });
  const [protectedActions, setProtectedActions] = useState({
    deleteClient: true,
    deleteDossier: true,
    deletePayment: true,
    viewSensitiveData: true,
    updateDeletionConfig: true,
    enableHardDelete: true,
    restoreArchivedItem: true
  });

  const fetchProtectionConfig = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Utilisateur non connecté : ne faire AUCUN appel API sensible au chargement
      return;
    }

    try {
      const handleQuietCatch = (err) => {
        if (err?.response?.status === 503) return null;
        return null;
      };

      const headers = getAuthHeaders();

      const [protRes, actionsRes, authRes] = await Promise.all([
        axios.get(`${API_URL}/system-settings/protection-config`, headers).catch(handleQuietCatch),
        axios.get(`${API_URL}/system-settings/sensitive-actions`, headers).catch(handleQuietCatch),
        axios.get(`${API_URL}/system-settings/exceptional-authorizations?status=active`, headers).catch(err => {
          if (err?.response?.status === 503) return { data: { data: [] } };
          return { data: { data: [] } };
        })
      ]);


      if (protRes?.data?.success && protRes.data?.data?.protectedActions) {
        setProtectedActions(protRes.data.data.protectedActions);
      }

      if (actionsRes?.data?.success && actionsRes.data?.data) {
        if (Array.isArray(actionsRes.data.data.actions)) {
          setActionsCatalog(actionsRes.data.data.actions);
        }
        if (actionsRes.data.data.security) {
          setSecurityStatus(actionsRes.data.data.security);
        }
      }

      if (authRes?.data?.success && Array.isArray(authRes.data?.data)) {
        setUserExceptionalAuths(authRes.data.data);
      }
    } catch (err) {
      console.error('Erreur chargement protection & actions config:', err);
    }
  }, []);

  useEffect(() => {
    fetchProtectionConfig();

    const handleAuthChange = () => {
      fetchProtectionConfig();
    };

    window.addEventListener('sastra_auth_change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);

    return () => {
      window.removeEventListener('sastra_auth_change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [fetchProtectionConfig]);

  const lock = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setRemainingMs(0);
    setLastSuccess('');
  }, []);

  const refreshFromStorage = useCallback(() => {
    const stored = readStoredSession();
    setSession(stored);
    setRemainingMs(getRemainingMs(stored?.expiresAt));
  }, []);

  useEffect(() => {
    refreshFromStorage();
    const intervalId = window.setInterval(() => {
      const stored = readStoredSession();
      if (!stored) {
        lock();
        return;
      }
      setSession(stored);
      setRemainingMs(getRemainingMs(stored.expiresAt));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [lock, refreshFromStorage]);

  useEffect(() => {
    if (!lastSuccess) return undefined;
    const timeoutId = window.setTimeout(() => setLastSuccess(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [lastSuccess]);

  const unlock = useCallback(async (password, customDurationMinutes = 15) => {
    try {
      const response = await axios.post(
        `${API_URL}/system-settings/verify-protection-password`,
        { password },
        getAuthHeaders()
      );

      if (response.data?.success) {
        const durationMs = (customDurationMinutes || 15) * 60 * 1000;
        const expiresAt = Date.now() + durationMs;
        const nextSession = { unlocked: true, expiresAt };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
        setSession(nextSession);
        setRemainingMs(getRemainingMs(expiresAt));
        setLastError('');
        setLastSuccess(`Contenu sensible déverrouillé pour ${customDurationMinutes} minute(s).`);
        fetchProtectionConfig();
        return true;
      } else {
        setLastError(response.data?.message || WARNING_MESSAGE);
        setLastSuccess('');
        return false;
      }
    } catch (err) {
      console.error('Erreur vérification mot de passe:', err);
      const msg = err.response?.data?.message || WARNING_MESSAGE;
      setLastError(msg);
      setLastSuccess('');
      fetchProtectionConfig();
      return false;
    }
  }, [fetchProtectionConfig]);

  const clearMessages = useCallback(() => {
    setLastError('');
    setLastSuccess('');
  }, []);

  const getCurrentUserId = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        return String(u._id || u.id || '');
      }
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return String(payload.id || payload._id || '');
      }
    } catch {
      return '';
    }
    return '';
  };

  const isAuthValidAndActive = (auth) => {
    if (!auth || !auth.isActive) return false;
    const now = new Date();
    if (auth.startAt && new Date(auth.startAt) > now) return false;
    if (!auth.permanent && auth.expiresAt && new Date(auth.expiresAt) <= now) return false;
    return true;
  };

  const isAuthMatchingUser = (auth) => {
    if (!auth) return false;
    const currentId = getCurrentUserId();
    const authUserId = String(auth.userId?._id || auth.userId?.id || auth.userId || '');
    if (!currentId || !authUserId) return true;
    return authUserId === currentId;
  };

  const isAuthMatchingScope = (auth, targetCode, moduleName) => {
    const cleanTargetCode = String(targetCode || '').trim().toUpperCase();
    const cleanModuleName = String(moduleName || '').trim().toLowerCase();

    const fallbackModules = {
      AUDIT_LOGS_VIEW: ['systeme', 'audit', 'logs', 'system'],
      SETTINGS_UPDATE: ['systeme', 'settings', 'config', 'system'],
      ARCHIVE_RESTORE: ['systeme', 'archives', 'dossiers', 'system'],
      FINANCE_VIEW: ['finance', 'dashboard', 'systeme']
    };

    const targetModules = Array.from(new Set([
      cleanModuleName,
      ...(fallbackModules[cleanTargetCode] || [])
    ])).filter(Boolean);

    const hasEmptyScope = (!auth.actionCodes || auth.actionCodes.length === 0) && (!auth.modules || auth.modules.length === 0);
    const hasGlobalWildcard = (Array.isArray(auth.actionCodes) && (auth.actionCodes.includes('*') || auth.actionCodes.includes('ALL'))) ||
                              (Array.isArray(auth.modules) && (auth.modules.includes('*') || auth.modules.includes('all') || auth.modules.includes('ALL')));

    if (hasEmptyScope || hasGlobalWildcard) return true;

    const hasCode = Array.isArray(auth.actionCodes) && auth.actionCodes.map(c => String(c).toUpperCase()).includes(cleanTargetCode);
    if (hasCode) return true;

    const hasModule = Array.isArray(auth.modules) && auth.modules.some(m => {
      const cleanM = String(m).toLowerCase();
      return targetModules.includes(cleanM);
    });

    return hasModule;
  };

  const getMatchingExceptionalAuth = useCallback((targetCode, moduleName) => {
    return userExceptionalAuths.find(auth => {
      if (!isAuthValidAndActive(auth)) return false;
      if (!isAuthMatchingUser(auth)) return false;
      return isAuthMatchingScope(auth, targetCode, moduleName);
    });
  }, [userExceptionalAuths]);

  const checkActionProtected = useCallback((actionKeyOrCode) => {
    if (!actionKeyOrCode) return false;
    const clean = String(actionKeyOrCode).trim().toUpperCase();

    const legacyMap = {
      DELETECLIENT: 'CLIENT_DELETE',
      DELETEDOSSIER: 'DOSSIER_DELETE',
      DELETEPAYMENT: 'PAYMENT_DELETE',
      RESTOREARCHIVEDITEM: 'ARCHIVE_RESTORE',
      ENABLEHARDDELETE: 'HARD_DELETE_ENABLE',
      UPDATEDELETIONCONFIG: 'SETTINGS_UPDATE'
    };
    const targetCode = legacyMap[clean] || clean;

    const catalogItem = actionsCatalog.find(a => a.actionCode.toUpperCase() === targetCode);
    const moduleName = catalogItem?.module || '';

    const matchingAuth = getMatchingExceptionalAuth(targetCode, moduleName);

    // 1. Si une Autorisation Exceptionnelle valide existe pour cet utilisateur -> PAS DE DEMANDE DE CODE SECRET
    if (matchingAuth) {
      return false; // Accès direct sans popup !
    }

    // 2. 3 Modules critiques exigeant le code secret par défaut s'il n'y a pas d'autorisation exceptionnelle
    const CRITICAL_MODULE_ACTIONS = ['SETTINGS_UPDATE', 'ARCHIVE_RESTORE', 'AUDIT_LOGS_VIEW'];
    if (CRITICAL_MODULE_ACTIONS.includes(targetCode)) {
      return true;
    }

    // 3. Configuration standard pour toutes les autres actions
    if (catalogItem) {
      return Boolean(catalogItem.enabled && catalogItem.requirePassword);
    }

    return Boolean(protectedActions[actionKeyOrCode] ?? false);
  }, [actionsCatalog, protectedActions, getMatchingExceptionalAuth]);

  const getActionConfig = useCallback((actionCode) => {
    if (!actionCode) return null;
    const targetCode = String(actionCode).trim().toUpperCase();
    const catalogItem = actionsCatalog.find(a => a.actionCode.toUpperCase() === targetCode);
    if (!catalogItem) return null;

    const moduleName = catalogItem.module || '';
    const matchingAuth = getMatchingExceptionalAuth(targetCode, moduleName);

    if (matchingAuth) {
      return {
        ...catalogItem,
        enabled: matchingAuth.enableDisabledFeatures ? true : catalogItem.enabled,
        requirePassword: matchingAuth.bypassSecretCode ? false : catalogItem.requirePassword
      };
    }

    return catalogItem;
  }, [actionsCatalog, getMatchingExceptionalAuth]);

  const isActionVisible = useCallback((actionCode) => {
    if (!actionCode) return true;
    const clean = String(actionCode).trim().toUpperCase();

    const legacyMap = {
      DELETECLIENT: 'CLIENT_DELETE',
      DELETEDOSSIER: 'DOSSIER_DELETE',
      DELETEPAYMENT: 'PAYMENT_DELETE',
      RESTOREARCHIVEDITEM: 'ARCHIVE_RESTORE',
      ENABLEHARDDELETE: 'HARD_DELETE_ENABLE',
      UPDATEDELETIONCONFIG: 'SETTINGS_UPDATE'
    };
    const targetCode = legacyMap[clean] || clean;

    const catalogItem = actionsCatalog.find(a => a.actionCode.toUpperCase() === targetCode);
    const moduleName = catalogItem?.module || '';

    const matchingAuth = getMatchingExceptionalAuth(targetCode, moduleName);

    // 1. Contrôle de l'activation globale (enabled = false)
    let isEnabled = catalogItem ? catalogItem.enabled : true;
    if (matchingAuth && matchingAuth.enableDisabledFeatures) {
      isEnabled = true;
    }
    if (!isEnabled) {
      return false; // Action DÉSACTIVÉE -> MASQUÉE / INVISIBLE !
    }

    // 2. Contrôle de l'exigence du code secret (requirePassword)
    const CRITICAL_MODULE_ACTIONS = ['SETTINGS_UPDATE', 'ARCHIVE_RESTORE', 'AUDIT_LOGS_VIEW'];
    const isCritical = CRITICAL_MODULE_ACTIONS.includes(targetCode);

    let isPasswordRequired = isCritical || Boolean(catalogItem?.requirePassword);
    if (matchingAuth && (matchingAuth.bypassSecretCode !== false || matchingAuth.permanent || matchingAuth.isActive)) {
      isPasswordRequired = false;
    }

    if (!isPasswordRequired) {
      return true; // Secret Code Requis = OFF (ou bypass accordé) -> Visible par défaut
    }

    // Secret Code Requis = ON -> Exige OBLIGATOIREMENT une Autorisation Exceptionnelle valide pour être VISIBLE
    return Boolean(matchingAuth);
  }, [actionsCatalog, getMatchingExceptionalAuth]);


  const value = useMemo(() => {
    if (!SENSITIVE_LOCK_ENABLED) {
      return {
        isUnlocked: true,
        expiresAt: null,
        remainingMs: 0,
        lastError: '',
        lastSuccess: '',
        protectedActions,
        actionsCatalog,
        userExceptionalAuths,
        securityStatus,
        checkActionProtected: () => false,
        isActionVisible: () => true,
        getActionConfig: () => null,
        fetchProtectionConfig,
        unlock: async () => true,
        lock: () => {},
        clearMessages: () => {},
      };
    }

    return {
      isUnlocked: Boolean(session?.unlocked && getRemainingMs(session.expiresAt) > 0),
      expiresAt: session?.expiresAt || null,
      remainingMs,
      lastError,
      lastSuccess,
      protectedActions,
      actionsCatalog,
      userExceptionalAuths,
      securityStatus,
      checkActionProtected,
      isActionVisible,
      getActionConfig,
      fetchProtectionConfig,
      unlock,
      lock,
      clearMessages,
    };
  }, [clearMessages, lastError, lastSuccess, lock, remainingMs, session, unlock, protectedActions, actionsCatalog, userExceptionalAuths, securityStatus, checkActionProtected, isActionVisible, getActionConfig, fetchProtectionConfig]);


  return (
    <SensitiveDataLockContext.Provider value={value}>
      {children}
      {SENSITIVE_LOCK_ENABLED && value.isUnlocked && (
        <button className="sdl-floating-lock" type="button" onClick={lock} title="Verrouiller les contenus protégés">
          <Lock size={15} />
          <span>Verrouiller</span>
          <small>{Math.ceil(remainingMs / 60000)} min</small>
        </button>
      )}
      {SENSITIVE_LOCK_ENABLED && value.isUnlocked && lastSuccess && (
        <div className="sdl-success-toast">
          {lastSuccess}
        </div>
      )}
    </SensitiveDataLockContext.Provider>
  );
};

export const useProtectedDataLock = () => {
  const context = useContext(SensitiveDataLockContext);
  if (!context) {
    throw new Error('useProtectedDataLock must be used inside SensitiveDataLockProvider');
  }
  return context;
};
